import React from 'react';
import { 
  Heart, 
  Brain, 
  Users, 
  Shield, 
  CheckCircle, 
  ArrowRight,
  MapPin,
  Clock,
  Star,
  BookOpen,
  Stethoscope,
  Award
} from 'lucide-react';

const MentalHealthCareUKPage: React.FC = () => {

  const mentalHealthServices = [
    {
      title: 'NHS Mental Health Services',
      description: 'Free at the point of use for UK residents through the National Health Service',
      features: [
        'GP referral typically required',
        'Community Mental Health Teams',
        'Crisis intervention services',
        'Inpatient mental health units',
        'Talking therapies (IAPT)'
      ],
      waitTime: 'Can vary significantly by area',
      icon: <Shield className="h-8 w-8 text-blue-600" />
    },
    {
      title: 'Private Mental Health Care',
      description: 'Self-funded or insurance-covered private mental health services',
      features: [
        'No GP referral needed',
        'Shorter waiting times',
        'Choice of therapist/psychiatrist',
        'Flexible appointment times',
        'Range of therapeutic approaches'
      ],
      waitTime: 'Often within days or weeks',
      icon: <Heart className="h-8 w-8 text-red-600" />
    },
    {
      title: 'Employee Assistance Programs (EAP)',
      description: 'Workplace-provided mental health support services',
      features: [
        'Confidential counseling services',
        'Work-related stress support',
        '24/7 helplines available',
        'Manager guidance services',
        'Critical incident support'
      ],
      waitTime: 'Immediate access available',
      icon: <Users className="h-8 w-8 text-green-600" />
    },
    {
      title: 'Third Sector Organizations',
      description: 'Charity and voluntary sector mental health services',
      features: [
        'Peer support groups',
        'Specialized condition support',
        'Community-based services',
        'Advocacy and information',
        'Crisis support lines'
      ],
      waitTime: 'Varies by organization',
      icon: <BookOpen className="h-8 w-8 text-purple-600" />
    }
  ];

  const commonConditions = [
    {
      condition: 'Anxiety Disorders',
      prevalence: '1 in 6 people',
      description: 'Including generalized anxiety, panic disorder, phobias, and social anxiety',
      treatments: ['CBT', 'Counseling', 'Medication', 'Mindfulness']
    },
    {
      condition: 'Depression',
      prevalence: '1 in 10 people',
      description: 'Major depressive disorder affecting mood, energy, and daily functioning',
      treatments: ['Therapy', 'Antidepressants', 'Lifestyle changes', 'Support groups']
    },
    {
      condition: 'ADHD',
      prevalence: '2-5% of adults',
      description: 'Attention Deficit Hyperactivity Disorder affecting focus and behavior',
      treatments: ['Medication', 'Behavioral therapy', 'Coaching', 'Workplace adjustments']
    },
    {
      condition: 'Autism Spectrum Disorders',
      prevalence: '1 in 100 people',
      description: 'Neurodevelopmental conditions affecting communication and social interaction',
      treatments: ['Support services', 'Social skills training', 'Occupational therapy', 'Educational support']
    }
  ];

  const accessPathways = [
    {
      pathway: 'GP Referral',
      description: 'Traditional route through your General Practitioner',
      steps: [
        'Book appointment with GP',
        'Discuss mental health concerns',
        'GP assessment and referral',
        'Wait for specialist appointment',
        'Begin treatment'
      ],
      timeframe: '6-18 weeks typically'
    },
    {
      pathway: 'Self-Referral',
      description: 'Direct access to certain NHS services',
      steps: [
        'Identify local IAPT service',
        'Self-refer online or by phone',
        'Initial assessment call',
        'Treatment allocation',
        'Begin therapy sessions'
      ],
      timeframe: '2-6 weeks typically'
    },
    {
      pathway: 'Private Route',
      description: 'Direct access to private mental health services',
      steps: [
        'Research qualified practitioners',
        'Contact directly for appointment',
        'Initial consultation',
        'Treatment plan agreement',
        'Begin treatment immediately'
      ],
      timeframe: '1-2 weeks typically'
    },
    {
      pathway: 'Crisis Support',
      description: 'Emergency mental health support when needed',
      steps: [
        'Call crisis helpline',
        'Emergency department if immediate risk',
        'Crisis team assessment',
        'Safety planning',
        'Follow-up care arrangement'
      ],
      timeframe: 'Immediate response'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 flex items-center justify-center gap-3">
            <Brain className="h-12 w-12 text-blue-600" />
            What is Mental Health Care in the UK?
          </h1>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto mb-8">
            Understanding the mental health care landscape in the United Kingdom: services, access routes, 
            and support available for individuals seeking mental health treatment and support.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 justify-center mb-4">
              <Stethoscope className="h-6 w-6 text-blue-600" />
              <h2 className="text-lg font-semibold text-blue-800">Mental Health in the UK</h2>
            </div>
            <p className="text-blue-700">
              Mental health care in the UK is delivered through a mixed economy of NHS services, private providers, 
              workplace programs, and third sector organizations. Understanding your options can help you access 
              the right support at the right time.
            </p>
          </div>
        </div>

        {/* Mental Health Service Types */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Types of Mental Health Services in the UK
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mentalHealthServices.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-3 mb-6">
                  {service.icon}
                  <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                </div>
                
                <p className="text-gray-700 mb-6">{service.description}</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-600">Typical Wait Time:</span>
                    </div>
                    <p className="text-gray-800 font-semibold">{service.waitTime}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common Mental Health Conditions */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Common Mental Health Conditions Treated
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {commonConditions.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Star className="h-6 w-6 text-yellow-500" />
                  <h3 className="text-lg font-bold text-gray-900">{item.condition}</h3>
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                    {item.prevalence}
                  </span>
                </div>
                
                <p className="text-gray-700 mb-4">{item.description}</p>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Common Treatments:</h4>
                  <div className="flex flex-wrap gap-2">
                    {item.treatments.map((treatment, idx) => (
                      <span 
                        key={idx}
                        className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                      >
                        {treatment}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Access Pathways */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            How to Access Mental Health Care in the UK
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {accessPathways.map((pathway, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{pathway.pathway}</h3>
                <p className="text-gray-700 mb-6">{pathway.description}</p>
                
                <div className="space-y-3 mb-6">
                  {pathway.steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                        {idx + 1}
                      </div>
                      <span className="text-gray-700">{step}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-600">Timeframe:</span>
                  </div>
                  <p className="text-gray-800 font-semibold">{pathway.timeframe}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Statistics */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Mental Health in the UK: Key Facts
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">1 in 4</div>
                <p className="text-gray-700">People experience mental health problems each year</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">£15.1bn</div>
                <p className="text-gray-700">Annual NHS mental health budget (2023/24)</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">2.3M</div>
                <p className="text-gray-700">People in contact with adult mental health services</p>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                Mental Health Parity
              </h3>
              <p className="text-gray-700 text-center max-w-4xl mx-auto">
                The UK government has committed to achieving parity of esteem between mental and physical health, 
                ensuring mental health receives equal priority in terms of funding, service development, and treatment options.
              </p>
            </div>
          </div>
        </div>

        {/* Getting Help */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need Mental Health Support?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              If you're experiencing mental health difficulties, remember that help is available. 
              Don't wait for a crisis - early intervention often leads to better outcomes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-red-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-bold text-red-800 mb-3">Emergency Support</h3>
              <div className="space-y-2 text-red-700">
                <p><strong>999</strong> - Life-threatening emergency</p>
                <p><strong>111</strong> - NHS urgent mental health helpline</p>
                <p><strong>116 123</strong> - Samaritans (24/7)</p>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-bold text-blue-800 mb-3">NHS Services</h3>
              <div className="space-y-2 text-blue-700">
                <p>Contact your GP</p>
                <p>NHS.uk for self-referral</p>
                <p>Local IAPT services</p>
              </div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-bold text-green-800 mb-3">Private Support</h3>
              <div className="space-y-2 text-green-700">
                <p>Private therapists/psychiatrists</p>
                <p>Employee assistance programs</p>
                <p>Mental health charities</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentalHealthCareUKPage;